import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CookieBanner } from "@/components/CookieBanner";
import { Reveal } from "@/components/Reveal";
import { usePageMeta } from "@/lib/api";

const PageShell = ({ title, children, testId }) => (
  <div className="App">
    <Header />
    <main className="bg-white min-h-screen">
      <div className="bg-slate-950 text-white hero-grain">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
          <h1 className="font-display text-3xl font-extrabold tracking-tight" data-testid={testId}>{title}</h1>
        </div>
      </div>
      <Reveal>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 text-sm sm:text-base text-slate-600 leading-relaxed space-y-4 [&_h2]:font-display [&_h2]:text-slate-900 [&_h2]:font-bold [&_h2]:text-lg [&_h2]:pt-4">
          {children}
        </div>
      </Reveal>
    </main>
    <Footer />
    <CookieBanner />
  </div>
);

export function AvisoLegal() {
  usePageMeta("Aviso legal · solsdiarias360", "Aviso legal de solsdiarias360, sitio web independiente de recomendaciones de productos.");
  return (
    <PageShell title="Aviso legal" testId="legal-notice-title">
      <p>
        Este aviso legal regula el uso del sitio web solsdiarias360 (en adelante, «el sitio»).
        Titular del sitio: <strong>[Nombre o denominación del titular]</strong>, con email de
        contacto disponible en la página de contacto. Estos datos se completarán antes de la
        puesta en producción definitiva del sitio.
      </p>
      <h2>Objeto</h2>
      <p>
        solsdiarias360 es un sitio web independiente de recomendaciones de productos. No vende
        productos directamente ni es la tienda oficial de Amazon: enlaza a Amazon.es, donde se
        completa cualquier compra bajo las condiciones de Amazon.
      </p>
      <h2>Propiedad intelectual</h2>
      <p>
        Los textos, el diseño y los contenidos originales del sitio pertenecen a su titular. Las
        imágenes proceden de bancos de imágenes con licencia de uso o de los propios fabricantes,
        y las marcas citadas pertenecen a sus respectivos propietarios.
      </p>
      <h2>Responsabilidad</h2>
      <p>
        La información sobre precios, características y disponibilidad de los productos es
        orientativa y puede variar. La información aplicable es la que muestre Amazon.es en el
        momento de la compra. El titular no se responsabiliza de las compras realizadas en sitios
        de terceros enlazados desde esta web.
      </p>
    </PageShell>
  );
}

export function Privacidad() {
  usePageMeta("Política de privacidad · solsdiarias360", "Cómo trata solsdiarias360 los datos personales conforme al RGPD.");
  return (
    <PageShell title="Política de privacidad" testId="privacy-title">
      <p>
        Responsable del tratamiento: <strong>[Nombre o denominación del titular]</strong> (datos
        identificativos pendientes de completar antes del lanzamiento definitivo). Puedes ejercer
        tus derechos escribiendo a través del formulario de contacto.
      </p>
      <h2>Qué datos recogemos</h2>
      <p>
        A través del formulario de contacto recogemos tu nombre, tu dirección de email y el
        contenido de tu mensaje, con la única finalidad de responderte. No creamos perfiles ni
        cedemos tus datos a terceros.
      </p>
      <h2>Enlaces a terceros</h2>
      <p>
        Al pulsar los enlaces de producto sales de este sitio hacia Amazon.es, que tratará tus
        datos conforme a su propia política de privacidad. Te recomendamos leerla antes de
        comprar.
      </p>
      <h2>Tus derechos</h2>
      <p>
        Conforme al RGPD (UE) 2016/679 y a la LOPDGDD 3/2018, puedes ejercer los derechos de
        acceso, rectificación, supresión, oposición, limitación y portabilidad. También puedes
        reclamar ante la Agencia Española de Protección de Datos (aepd.es).
      </p>
    </PageShell>
  );
}

export function Cookies() {
  usePageMeta("Política de cookies · solsdiarias360", "Información sobre las cookies que utiliza solsdiarias360 y cómo gestionarlas.");
  return (
    <PageShell title="Política de cookies" testId="cookies-title">
      <p>
        Este sitio utiliza cookies propias y de terceros. Al entrar por primera vez puedes
        aceptarlas, rechazarlas o configurarlas desde el aviso de consentimiento, y cambiar tu
        decisión en cualquier momento desde «Preferencias de cookies» en el pie de página.
      </p>
      <h2>Tipos de cookies</h2>
      <p>
        <strong>Esenciales:</strong> necesarias para que la web funcione (por ejemplo, recordar tu
        decisión de consentimiento). <strong>De análisis:</strong> nos ayudan a entender qué
        secciones se visitan para mejorar el contenido; solo se activan con tu consentimiento.
        <strong> De publicidad:</strong> permitirían medir campañas; solo se activan con tu
        consentimiento.
      </p>
      <h2>Cómo desactivarlas</h2>
      <p>
        Además de nuestro panel de preferencias, puedes bloquear o eliminar las cookies desde la
        configuración de tu navegador. Ten en cuenta que algunas funciones podrían dejar de estar
        disponibles.
      </p>
    </PageShell>
  );
}

export function Afiliacion() {
  usePageMeta("Declaración de afiliación · solsdiarias360", "Declaración de afiliación de solsdiarias360 con el programa de afiliados de Amazon.");
  return (
    <PageShell title="Declaración de afiliación" testId="affiliate-title">
      <p>
        <strong>Como asociado de Amazon, solsdiarias360 obtiene ingresos por las compras
        adscritas que cumplen los requisitos aplicables.</strong>
      </p>
      <h2>Qué significa esto</h2>
      <p>
        Algunos enlaces de esta web son enlaces de afiliado: si compras a través de ellos, podemos
        recibir una pequeña comisión de Amazon. Esto no supone ningún coste adicional para ti: el
        precio que pagas es exactamente el mismo.
      </p>
      <h2>Nuestra independencia</h2>
      <p>
        solsdiarias360 es un sitio independiente de recomendaciones y no es la tienda oficial de
        Amazon ni está gestionado por Amazon. Las recomendaciones se basan en la utilidad del
        producto para resolver problemas cotidianos. No publicamos reseñas falsas, descuentos
        inventados ni falsas urgencias.
      </p>
      <h2>Precios y disponibilidad</h2>
      <p>
        Los precios y la disponibilidad pueden cambiar en Amazon.es en cualquier momento. El
        precio aplicable es el mostrado por Amazon en el momento de la compra.
      </p>
    </PageShell>
  );
}

export function SobreNosotros() {
  usePageMeta("Sobre nosotros · solsdiarias360", "Qué es solsdiarias360 y cómo seleccionamos los productos que recomendamos.");
  return (
    <PageShell title="Sobre nosotros" testId="about-title">
      <p>
        solsdiarias360 nace con una idea sencilla: <strong>soluciones inteligentes para cada
        día</strong>. Nos dedicamos a buscar y ordenar productos prácticos que resuelven los
        pequeños problemas de la vida cotidiana: la cocina desordenada, los cables sueltos, el
        maletero caótico o la maleta imposible.
      </p>
      <h2>Cómo trabajamos</h2>
      <p>
        Cada producto que aparece en la web se describe por el problema que resuelve, no por
        promesas de marketing. No inventamos precios, valoraciones ni descuentos: cuando un dato
        no está verificado, lo indicamos claramente.
      </p>
      <h2>Cómo nos financiamos</h2>
      <p>
        Como asociados de Amazon, obtenemos ingresos por las compras adscritas que cumplen los
        requisitos aplicables. Comprar a través de nuestros enlaces no te cuesta más y nos ayuda
        a mantener el proyecto.
      </p>
    </PageShell>
  );
}
